/**
 * Works out where a not-yet-sent event will land in a claim's timeline.
 *
 * The machine always *appends* an event, but the timeline is ordered by `eventDate` — so a
 * backdated event is rendered in the middle of the history. Before TECH-893 that happened
 * silently, which is how a twelve-minute ordering error (TECH-892) survived unnoticed. This
 * powers the preview panel that shows the admin the consequence before they confirm.
 *
 * Pure and free of React so the arithmetic can be tested on its own.
 */

/** Minimum an event needs for positioning — keeps this usable from any shape of query. */
export type PositionableEvent = {
  id: string;
  eventDate: string | Date;
  eventType: { displayName: string };
};

export type InsertionNeighbours<T extends PositionableEvent> = {
  /** Newest event at or before the target — the one the new event will follow. */
  before?: T;
  /** Oldest event after the target — the one the new event will precede. */
  after?: T;
  /**
   * True when some existing event is dated after the target, i.e. the new event will *not*
   * land last. Always equivalent to `after !== undefined`; named for the warning it drives.
   */
  landsBeforeLast: boolean;
};

function timeOf(event: PositionableEvent): number {
  return new Date(event.eventDate).getTime();
}

/**
 * Splits the claim's events around `target`.
 *
 * An event dated exactly on an existing one counts as landing *after* it, matching
 * `sortClaimEventChronologically` on the API, which breaks `eventDate` ties by `createdAt` —
 * and the new event is always the most recently created.
 */
export function findInsertionNeighbours<T extends PositionableEvent>(
  events: T[],
  target: Date,
): InsertionNeighbours<T> {
  const targetTime = target.getTime();
  // Copy before sorting: `claim.claimEvents` is shared with the timeline render.
  const ascending = [...events].sort((a, b) => timeOf(a) - timeOf(b));

  const before = ascending.filter((event) => timeOf(event) <= targetTime).at(-1);
  const after = ascending.find((event) => timeOf(event) > targetTime);

  return { before, after, landsBeforeLast: after !== undefined };
}
